<template>
  <div
    :id="id"
    class="LKPublicationsFormdataTableInput"
    @keydown.left="onHorizontal(-1)"
    @keydown.right="onHorizontal(1)"
    @keydown.up="onVertical(-1)"
    @keydown.down="onVertical(1)"
  >
    <span
      v-if="computedAttrs.componentName === 'span'"
      v-bind="computedAttrs.bind"
      v-text="computedAttrs.text"
    />
    <component
      :is="computedAttrs.componentName"
      v-else
      :ref="computedAttrs.ref"
      v-model="data[header.cell_uuid]"
      v-bind="computedAttrs.bind"
    >
      <template #info>
        <span class="LKPublicationsFormdataTableInput__info-sign">
          <BInfo
            v-if="showInfo"
            :disabled="!info.text"
            :icon="info.icon"
            :text="info.text"
            :size="info.size"
          />
        </span>
      </template>
    </component>
  </div>
</template>

<script>
import omitBy from 'lodash/omitBy'
import {
  EXCEL_CELL_ERROR_LABEL,
  FORM_TEMPLATE_CELL_ERROR_LABEL,
} from '@/helpers/enums/params'
import {
  SheetCellTypes,
  SheetCellTypeMethods,
  SheetCellDepsTypes,
  DEPENDENCE_EMPTY_VALUE,
} from '@/typings/api/FormTemplate'
import {
  formsHandbookMapper as StoreMapper,
} from '@/views/FormsHandbook/store/cache'

import BAutocompleteSelectHandbook from '@/components/forms/BAutocompleteCommon/BAutocompleteSelectHandbook.vue'
import BAutocompleteSelect from '@/components/forms/BAutocompleteCommon/BAutocompleteSelect.vue'
import BTextField from '@/components/forms/BTextField.vue'
import BTextArea from '@/components/forms/BTextarea.vue'
import BTextFieldDate from '@/components/forms/BTextFieldDate.vue'
import BTextFieldTime from '@/components/forms/BTextFieldTime.vue'
import BInfo from '@/components/common/BInfo.vue'
import { evaluateFormula } from '@/views/LKPublications/formdata/components/utils'
import { dateFormatterIgnoreTz } from '@/helpers/dateFormatter'

const getId = (value, index) => [`id-${value}`, `index-${index}`].join('#')

export default {
  name: 'LKPublicationsFormdataTableInput',
  components: {
    BAutocompleteSelectHandbook,
    BAutocompleteSelect,
    BTextArea,
    BTextField,
    BInfo,
    BTextFieldDate,
    BTextFieldTime,
  },
  inheritAttrs: false,
  props: {
    all_cells: {
      type: Array,
      required: true,
      validator: ([prop]) => !prop
        || (prop
          && 'uuid' in prop
          && '$index' in prop
          && '$sheet' in prop
          && '$data' in prop.$sheet),
    },
    headers: {
      type: Array,
      required: true,
    },
    header: {
      type: Object,
      required: true,
      validator: (prop) => prop && 'cell_uuid' in prop && '$cell' in prop,
    },
    data: {
      type: Object,
      required: true,
      validator: (prop) => prop,
    },
    index: {
      type: Number,
      required: true,
    },
    disabled: Boolean,
    /**
     * Данные для зависимости от других листов
     */
    maxValue: {
      type: Number,
      default: 0,
    },
    verticalLineData: {
      type: Object,
      default: () => {},
    },
    /**
     * Для проверок уникальности значений
     */
    verticalCellsData: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...StoreMapper.mapGetters(['getHandbookValues']),
    info() {
      return this.header.info
    },
    showInfo() {
      const { type_method } = this.header.$cell
      return this.header.info && SheetCellTypeMethods.auto_start !== type_method
    },
    computedAttrs() {
      let attrs = {}
      // eslint-disable-next-line object-curly-newline
      const { has, data, header, star } = this

      if (has.list || has.list_cell || has.dep_list) {
        attrs = {
          componentName: 'BAutocompleteSelect',
          ref: 'input',
          bind: {
            validate: this.validateRules,
            'error-icon': true,
            options: this.extendedListOptions,
            placeholder: `Список${star}`,
            clearable: false,
            disabled: this.computedDisabled || this.listOptions.length === 0,
            'autoselect-on-blur': true,
          },
        }
      } else if (has.list_handbook) {
        attrs = {
          componentName: 'BAutocompleteSelectHandbook',
          ref: 'input',
          bind: {
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Список${star}`,
            clearable: false,
            disabled: this.computedDisabled,
            handbook_id: header.$cell.list_handbook.id,
            'autoselect-on-blur': true,
          },
        }
      } else if (has.date_interval) {
        attrs = {
          componentName: 'BTextFieldDate',
          ref: 'input',
          bind: {
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Интервал${star}`,
            clearable: false,
            disabled: this.computedDisabled,
            min: header.$cell.date_from,
            max: header.$cell.date_to,
            formatReturned: 'DD.MM.YYYY',
          },
        }
      } else if (has.date) {
        attrs = {
          componentName: 'BTextFieldDate',
          ref: 'input',
          bind: {
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Дата${star}`,
            clearable: false,
            disabled: this.computedDisabled,
            min: header.$cell.date_from,
            max: header.$cell.date_to,
            formatReturned: 'DD.MM.YYYY',
          },
        }
      } else if (has.time) {
        attrs = {
          componentName: 'BTextFieldTime',
          ref: 'input',
          bind: {
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Время${star}`,
            clearable: false,
            disabled: this.computedDisabled,
          },
        }
      } else if (has.number || has.number_interval || has.schedule) {
        attrs = {
          componentName: 'BTextField',
          ref: 'input',
          bind: {
            'v-model': data[header.cell_uuid],
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Число${star}`,
            clearable: false,
            disabled: this.computedDisabled,
          },
        }
      } else if (has.string) {
        const isTextArea = header.$cell?.string_long
        attrs = {
          componentName: isTextArea ? 'BTextArea' : 'BTextField',
          ref: 'input',
          bind: {
            'v-model': data[header.cell_uuid],
            validate: this.validateRules,
            'error-icon': true,
            placeholder: `Текст${star}`,
            clearable: false,
            disabled: this.computedDisabled,
          },
        }
      } else if (has.formula) {
        attrs = {
          componentName: 'BTextField',
          ref: 'input',
          bind: {
            disabled: true,
            readonly: true,
            class: {
              LKPublicationsFormdataTableInput__formula: true,
              'mx-1': true,
              'is-error': this.isErrorFormula,
            },
            value: (data[header.cell_uuid] = this.formula),
          },
        }
      } else if (has.auto_start) {
        attrs = {
          componentName: 'span',
          bind: {
            class: 'mx-1',
          },
          text: (data[header.cell_uuid] = this.auto_start),
        }
      } else {
        attrs = {
          componentName: 'span',
          bind: {
            class: 'mx-1',
          },
          text: data[header.cell_uuid],
        }
      }

      return attrs
    },
    star() {
      return this.required ? '*' : ''
    },
    has() {
      const { $readonly } = this.data
      const { type, type_method } = this.header.$cell

      return {
        auto_start:
          type_method === SheetCellTypeMethods.auto_start && !$readonly,

        list:
          (type_method === SheetCellTypeMethods.list
            || type === SheetCellTypes.drop_list)
          && !$readonly,

        dep_list: type_method === SheetCellTypeMethods.dep_list && !$readonly,

        list_handbook: type === SheetCellTypes.list_handbook && !$readonly,

        list_cell: type === SheetCellTypes.list_cell && !$readonly,

        date_interval:
          type_method === SheetCellTypeMethods.date_interval && !$readonly,

        date:
          type === SheetCellTypes.date
          && type_method === SheetCellTypeMethods.manual
          && !$readonly,

        time:
          type === SheetCellTypes.time
          && type_method === SheetCellTypeMethods.manual
          && !$readonly,

        number_interval:
          type_method === SheetCellTypeMethods.number_interval && !$readonly,

        number:
          type === SheetCellTypes.number
          && type_method === SheetCellTypeMethods.manual
          && !$readonly,

        string: type === SheetCellTypes.string && !$readonly,

        formula: type === SheetCellTypes.formula && !$readonly,

        schedule: type === SheetCellTypes.schedule && !$readonly,
      }
    },
    hasDependence() {
      return !!this.header.$cell.dependence.type
    },
    dependenceValueInList() {
      if (!this.hasDependence) return false

      const validateResults = this.header.$cell.dependence.list.map(
        (dependence) => {
          const referenceCellValue = this.getCellValue(
            dependence.sheet_uuid,
            dependence.cell_uuid,
          )
          if (referenceCellValue === '') {
            return dependence.list.includes(DEPENDENCE_EMPTY_VALUE)
          }
          return dependence.list.includes(referenceCellValue)
        },
      )
      if (this.header.$cell.dependence.type === SheetCellDepsTypes.some) {
        return validateResults.some(Boolean)
      }
      return validateResults.every(Boolean)
    },
    required() {
      if (this.disabled || this.isPrefilled) {
        return false
      }
      if (this.hasDependence) {
        const valueInList = this.dependenceValueInList
        return this.header.$cell.required && valueInList
      }
      return !!this.header.$cell.required
    },
    disableDependenceValue() {
      return this.hasDependence && !this.dependenceValueInList
    },
    computedDisabled() {
      return this.disabled || this.disableDependenceValue || this.isPrefilled
    },
    isErrorFormula() {
      return [EXCEL_CELL_ERROR_LABEL, FORM_TEMPLATE_CELL_ERROR_LABEL].includes(
        this.formula,
      )
    },
    id() {
      return getId(this.header.cell_uuid, this.index)
    },
    formulaValuesMap: {
      get() {
        return this.calculateFormulaMappings()
      },
      set(newValue) {
        return newValue
      },
    },
    formula: {
      get() {
        return this.calculateFormulaValue()
      },
      set(newValue) {
        return newValue
      },
    },
    auto_start() {
      const { auto_start } = this.header.$cell
      return this.index + (+auto_start || 0)
    },
    listOptions() {
      const {
        list,
        list_cell,
        dep_list_sheet_uuid,
        dep_list_cell_uuid,
        dep_list_values,
      } = this.header.$cell

      if (this.has.list) return list
      if (this.has.list_handbook) return []
      if (this.has.dep_list) {
        return (
          dep_list_values[
            this.getCellValue(dep_list_sheet_uuid, dep_list_cell_uuid)
          ] || []
        )
      }
      if (!this.has.list_cell) return []

      const cell = this.all_cells.find((_) => _.uuid === list_cell)
      if (!cell) return []

      const { $sheet, $index } = cell

      return $sheet.$data
        .map((_) => _[$index])
        .filter((_) => ![void 0, null, ''].includes(_?.toString().trim()))
    },
    extendedListOptions() {
      const { listOptions, data, header } = this
      const value = data[header.cell_uuid]

      if (value && !listOptions.includes(value)) {
        return [value, ...listOptions]
      }

      return listOptions
    },
    validateRules() {
      const { has } = this
      const { $cell } = this.header

      let options = {
        required: this.required,
      }

      if (has.schedule) {
        options = {
          required: this.required,
          ruDecimal: true,
          min_value: 0,
          max_value: this.maxValue,
        }
      } else if (has.number) {
        options = {
          required: this.required,
          ruDecimal: true,
        }
      } else if (has.number_interval) {
        options = {
          required: this.required,
          ruDecimal: true,
          between: [$cell.number_from, $cell.number_to],
        }
      } else if (has.string) {
        options = {
          required: this.required,
          min: typeof $cell.string_min === 'number' ? $cell.string_min : null,
          max: typeof $cell.string_max === 'number' ? $cell.string_max : null,
        }
      } else if (has.date) {
        options = {
          required: this.required,
          date_format: 'dd.MM.yyyy',
        }
      } else if (has.date_interval) {
        options = {
          required: this.required,
          date_format: 'dd.MM.yyyy',
          date_between: [dateFormatterIgnoreTz($cell.date_from, 'DD.MM.yyyy'), dateFormatterIgnoreTz($cell.date_to, 'DD.MM.yyyy'), []],
        }
      }

      if (has.list || has.list_cell || has.dep_list) {
        options.included = this.listOptions
      }

      if (has.list_handbook) {
        const values = this.getHandbookValues(
          this.header.$cell.list_handbook.id,
        ) || []
        if (values.length) {
          options.included = [...values, ...values.map((v) => v.id)]
        }
      }

      if ($cell.unique) {
        options.unique = this.verticalCellsData
      }

      return omitBy(options, (value) => [void 0, null].includes(value))
    },
    isPrefilled() {
      const prefilledValue = this.header.$cell?.$sheet?.$prefilled_data?.[
        this.index
      ]?.[this.header.$cell.$index]
      return typeof prefilledValue !== 'undefined' && prefilledValue !== null
    },
  },
  watch: {
    disableDependenceValue(newVal, oldVal) {
      if (newVal === true && oldVal === false) {
        this.$set(this.data, this.header.cell_uuid, null)
      }
    },
    formulaValuesMap: {
      deep: true,
      handler() {
        if (!this.has.formula) return
        this.formula = this.calculateFormulaValue()
      },
    },
    data: {
      deep: true,
      handler() {
        if (!this.has.formula) return
        this.formulaValuesMap = this.calculateFormulaMappings()
        this.$emit('change')
      },
    },
  },
  mounted() {
  },
  methods: {
    ...StoreMapper.mapActions(['getOneById']),
    async onHorizontal(step) {
      const { selectionStart, value: data } = this.$el.querySelector('input')
      const headerIndex = this.headers.indexOf(this.header) + step
      const { value } = this.headers[headerIndex]

      const selector = `div[id="${getId(value, this.index)}"] input`
      const input = document.querySelector(selector)
      if (!input) return

      const isLast = step > 0 && selectionStart === data.length
      const isFirst = step < 0 && selectionStart === 0
      if (!isLast && !isFirst) return

      input.focus()
      const position = step > 0 ? 0 : input.value.length
      input.setSelectionRange?.(position, position)
      await new Promise((resolve) => setTimeout(resolve))
      input.setSelectionRange?.(position, position)

      this.$refs.input?.blur?.()
    },
    async onVertical(step = 1) {
      const index = this.index + step
      const selector = `div[id="${getId(this.header.cell_uuid, index)}"] input`
      const input = document.querySelector(selector)

      if (!input) return
      input.focus()
      input.setSelectionRange?.(0, 0)
      await new Promise((resolve) => setTimeout(resolve))
      input.setSelectionRange?.(0, 0)

      this.$refs.input?.blur?.()
    },
    getCellValue(sheet_uuid, cell_uuid) {
      const referenceCell = this.all_cells.find(
        (cell) => cell.uuid === cell_uuid,
      )

      if (sheet_uuid in this.verticalLineData) {
        return (
          this.verticalLineData[sheet_uuid][referenceCell.$index] || ''
        ).toString()
      }
      return (this.data[referenceCell.$index] || '').toString()
    },
    calculateFormulaValue() {
      if (!this.has.formula) return null

      const { data, formulaValuesMap } = this

      const formula = this.header.$cell.formula?.replace(
        /\[#\$_(.+?)#\]/g,
        (match, uuid) => {
          function intOrZero(val) {
            if (val === '') return 0
            // eslint-disable-next-line eqeqeq
            if (typeof val === 'string') {
              // eslint-disable-next-line no-param-reassign
              val = val.replace(',', '.')
            }

            // eslint-disable-next-line eqeqeq
            return val == +val ? val : 0
          }
          if (data[uuid]) {
            return intOrZero(data[uuid])
          }

          return intOrZero(formulaValuesMap[uuid])
        },
      )

      return evaluateFormula(formula)
    },
    calculateFormulaMappings() {
      if (!this.has.formula) return null

      const formulaValuesMap = {}
      const {
        all_cells,
        index,
        data,
        header: { $cell },
        headers,
      } = this
      const formulaUuids = [...$cell.formula?.matchAll(/\[#\$_(.+?)#\]/g)].map(
        ([, uuid]) => uuid,
      )
      const currItemUuids = headers.map((h) => h.$cell.uuid)

      all_cells.forEach((cell) => {
        if (formulaUuids.includes(cell.uuid)) {
          if (currItemUuids.includes(cell.uuid)) {
            formulaValuesMap[cell.uuid] = data[cell.$index]
          } else {
            const { $data } = cell.$sheet
            formulaValuesMap[cell.uuid] = $data?.[index]?.[cell.$index]
          }
        }
      })

      return formulaValuesMap
    },
  },
}
</script>

<style lang="scss">
.LKPublicationsFormdataTableInput {
  position: relative;

  input {
    font-size: 14px !important;
  }

  .v-input__slot {
    min-height: 32px !important;
    padding: 0 6px !important;
  }

  .v-input__prepend-inner,
  .v-input__append-inner {
    align-self: center;
    margin-top: 0 !important;
  }

  fieldset {
    // margin-right: 1px;
    margin: -1px;
    border-color: transparent;
    border-width: 1px !important;
    border-radius: 0;
  }

  &__formula {
    margin: 4px 8px;
    font-weight: bold;

    &.is-error {
      color: $webflow-color-white;
    }
  }

  &__info-sign {
    display: flex;
    align-items: center;
    margin-right: 5px;
    margin-left: 5px;
  }
}
</style>
